"use client";

import { useEffect, useState, type RefObject } from "react";

type Options = {
  /**
   * Grow the viewport before deciding something is visible, so a carousel is already
   * running by the time it scrolls into view rather than starting from a standstill.
   */
  rootMargin?: string;
  threshold?: number;
};

/**
 * True while `ref` is inside (or near) the viewport.
 *
 * The point is to gate `setInterval` auto-advance on visibility. Carousels here start an
 * interval on mount and never stop: on a 24-widget page that is a steady stream of React
 * commits for content nobody is looking at — measured at ~1.7 commits/second on
 * `online-mba`, each re-rendering ~25 components. It costs main-thread time during load,
 * INP afterwards, and battery for the whole visit. `ProgramBenefits2` already gates its
 * auto-scroll; this is that idea made reusable.
 *
 * SSR: returns `false` on the server and on the first client render, so the markup matches
 * and hydration is clean. The observer only resolves afterwards — one extra commit, which
 * is the same trade `RevealOnScroll` makes.
 *
 * No `IntersectionObserver` (very old browsers, some test environments) means we cannot
 * tell, so we report `true` and let the carousel run. A widget that silently stops
 * animating is a worse failure than one that animates off-screen.
 */
export function useIsInView<T extends Element>(
  ref: RefObject<T | null>,
  { rootMargin = "200px", threshold = 0 }: Options = {},
): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin, threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin, threshold]);

  return inView;
}

export default useIsInView;
